import { forwardRef, type ReactNode, type ReactElement, type PropsWithChildren } from 'react'
import { cn } from '@util'
import { createPolymorphicComponent, Group, Radio, type RadioCardProps, Stack, Text } from '@mantine/core'
import { LabelDetails } from '../listItem/LabelDetails'
import classes from './TRadioCard.module.css'

type LabelProps = { children: ReactNode }
type DescriptionProps = { children: ReactNode }

const Label = ({ children }: LabelProps): ReactElement => (
  <Text fw='bold' size='md' lh={1.3}>
    {children}
  </Text>
)

const Description = ({ children }: DescriptionProps): ReactElement => (
  <Text size='xs' c='dimmed' mt={4}>
    {children}
  </Text>
)

export type TRadioCardProps = PropsWithChildren<
  Omit<RadioCardProps, 'classNames' | 'styles' | 'vars'> & {
    className?: string
    label: string
    description?: string
  }
>

const _TRadioCard = forwardRef<HTMLButtonElement, TRadioCardProps>(
  ({ value, disabled, className, label, description, children, ...props }, ref) => (
    <Radio.Card
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(classes.root, className)}
      aria-disabled={disabled || undefined}
      mod={{ disabled }}
      {...props}
    >
      <Group wrap='nowrap' align='flex-start'>
        <Radio.Indicator />
        <Stack gap='xs'>
          <LabelDetails label={label} details={description} />
          {children}
        </Stack>
      </Group>
    </Radio.Card>
  )
)

_TRadioCard.displayName = 'TRadioCard'

export const TRadioCard = Object.assign(createPolymorphicComponent<'button', TRadioCardProps>(_TRadioCard), {
  Label,
  Description,
})
